<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import clubAccessService, { type PublicClubAccessPayment } from '../services/club-access.service'
import LanguageSelector from '../components/LanguageSelector.vue'
import { useAuthStore } from '../stores/auth'

const route = useRoute()
const { t, locale } = useI18n()
const authStore = useAuthStore()
const isLoggedIn = !!localStorage.getItem('auth_token')
const payment = ref<PublicClubAccessPayment | null>(null)
const loading = ref(true)
const error = ref(false)

// Document acceptances
const acceptTerms = ref(false)
const acceptPrivacy = ref(false)
const acceptAppTerms = ref(false)

// Payer name — pre-fill from logged-in person
const currentPerson = authStore.initData?.persons?.current
const payerName = ref(
  [currentPerson?.first_name, currentPerson?.last_name].filter(Boolean).join(' ') || ''
)

// Payer email — pre-fill from query param, then logged-in user
const payerEmail = ref((route.query.email as string) || authStore.user?.email || '')
const processingPayment = ref(false)

const hash = computed(() => route.params.hash as string)
const currentUrl = computed(() => window.location.href)
const returnStatus = computed(() => route.query.status as string | undefined)

// Payment processing state
const waitingForPayment = ref(false)
const pollingInterval = ref<number | null>(null)

// Auto-refresh countdown for unpaid payments
const countdown = ref(15)
let countdownInterval: any = null

const startCountdown = () => {
  if (countdownInterval) {
    clearInterval(countdownInterval)
    countdownInterval = null
  }
  if (payment.value?.status === 'PENDING' || payment.value?.status === 'PROCESSING') {
    countdownInterval = setInterval(async () => {
      countdown.value--
      if (countdown.value <= 0) {
        countdown.value = 15
        await fetchPayment()
      }
    }, 1000)
  }
}

const allDocumentsAccepted = computed(() => {
  const emailValid = payerEmail.value.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payerEmail.value)
  const nameRequired = !isLoggedIn || !currentPerson?.first_name
  const nameValid = !nameRequired || payerName.value.trim() !== ''
  return emailValid && nameValid && acceptTerms.value && acceptPrivacy.value && acceptAppTerms.value
})

const hasOnlinePayment = computed(() => {
  const providers = payment.value?.available_providers ?? []
  return providers.length > 0
})

// Fetch payment data
const fetchPayment = async () => {
  if (!hash.value || hash.value === 'undefined') return

  loading.value = true
  error.value = false

  try {
    const response = await clubAccessService.getPublicPayment(hash.value)
    payment.value = response.payment

    startCountdown()
  } catch (err: any) {
    console.error('Error fetching payment:', err)
    error.value = true
  } finally {
    loading.value = false
  }
}

// Show toast notification
const showToast = (message: string, type: 'success' | 'error' = 'success') => {
  const toast = document.createElement('div')
  toast.className = `fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg text-white text-sm font-medium z-50 transform transition-all duration-300 ${type === 'success' ? 'bg-green-600' : 'bg-red-600'}`
  toast.textContent = message
  toast.style.opacity = '0'
  toast.style.transform = 'translateY(20px)'

  document.body.appendChild(toast)

  setTimeout(() => {
    toast.style.opacity = '1'
    toast.style.transform = 'translateY(0)'
  }, 10)

  setTimeout(() => {
    toast.style.opacity = '0'
    toast.style.transform = 'translateY(20px)'
    setTimeout(() => {
      document.body.removeChild(toast)
    }, 300)
  }, 3000)
}

// Copy payment URL
const copyPaymentUrl = () => {
  const url = window.location.href
  navigator.clipboard.writeText(url)
    .then(() => {
      showToast(t('publicPayment.linkCopied'))
    })
    .catch((err) => {
      console.error('Failed to copy: ', err)
      showToast(t('publicPayment.copyLinkFailed'), 'error')
    })
}

// Format amount to 2 decimal places
const formatAmount = (amount: number) => {
  return parseFloat(amount.toString()).toFixed(2)
}

// Format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString(({ pl: 'pl-PL', en: 'en-US', fr: 'fr-FR', es: 'es-ES', de: 'de-DE', it: 'it-IT', pt: 'pt-PT', cs: 'cs-CZ', nl: 'nl-NL', sv: 'sv-SE', da: 'da-DK', hu: 'hu-HU', ro: 'ro-RO', no: 'nb-NO', lt: 'lt-LT', hr: 'hr-HR', fi: 'fi-FI' }[locale.value] || 'en-US'), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Online payment handler - initiate payment with selected provider
const handlePayOnline = async (provider: 'PAYU' | 'STRIPE') => {
  if (!allDocumentsAccepted.value || processingPayment.value) return

  processingPayment.value = true

  try {
    const response = await clubAccessService.initiatePublicPayment(hash.value, {
      payer_email: payerEmail.value.trim(),
      payer_name: payerName.value.trim(),
      provider: provider,
      return_url: `${window.location.origin}/public/access-payment/${hash.value}?status=processing`,
      cancel_url: `${window.location.origin}/public/access-payment/${hash.value}?status=cancelled`
    })

    // Redirect to payment URL
    if (response.payment_url) {
      window.location.href = response.payment_url
    } else {
      showToast(t('publicAccessPayment.errorNoPaymentLink'), 'error')
      processingPayment.value = false
    }
  } catch (err: any) {
    console.error('Error processing payment:', err)
    showToast(err.response?.data?.message || t('publicAccessPayment.errorInitiating'), 'error')
    processingPayment.value = false
  }
}

// Start polling payment status
const startPollingPaymentStatus = () => {
  pollingInterval.value = window.setInterval(async () => {
    try {
      const response = await clubAccessService.getPublicPayment(hash.value)
      payment.value = response.payment

      if (response.payment.status === 'COMPLETED') {
        stopPollingPaymentStatus()
        waitingForPayment.value = false
        showToast(t('publicAccessPayment.paymentProcessed'), 'success')
      }
    } catch (err) {
      console.error('Error polling payment status:', err)
    }
  }, 3000)
}

const stopPollingPaymentStatus = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value)
    pollingInterval.value = null
  }
}

onMounted(async () => {
  await fetchPayment()

  // Check if user returned from payment provider
  if (returnStatus.value === 'processing') {
    waitingForPayment.value = true
    startPollingPaymentStatus()
    showToast(t('publicAccessPayment.waitingForConfirmation'), 'success')
  } else if (returnStatus.value === 'cancelled') {
    showToast(t('publicAccessPayment.paymentCancelled'), 'error')
  }
})

onBeforeUnmount(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval)
  }
  stopPollingPaymentStatus()
})
</script>

<template>
  <div class="bg-gray-50 min-h-screen">
    <!-- Language Selector (only for unauthenticated users) -->
    <div v-if="!isLoggedIn" class="flex justify-end px-4 pt-4">
      <LanguageSelector />
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p class="mt-4 text-gray-600">{{ t('publicPayment.loading') }}</p>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="flex items-center justify-center min-h-screen">
      <div class="text-center max-w-md mx-auto px-4">
        <div class="text-6xl mb-4">❌</div>
        <h1 class="text-2xl font-bold text-gray-900 mb-2">{{ t('publicPayment.notFound') }}</h1>
        <p class="text-gray-600">{{ t('publicPayment.notFoundDescription') }}</p>
      </div>
    </div>

    <!-- Payment Details -->
    <div v-else-if="payment" class="max-w-2xl mx-auto px-4 py-8">
      <!-- Club Logo -->
      <div class="flex justify-center mb-6">
        <div v-if="payment.club_logo_url" class="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-lg overflow-hidden">
          <img
            :src="payment.club_logo_url"
            :alt="payment.club_name"
            class="w-full h-full object-cover"
          />
        </div>
        <div v-else class="w-24 h-24 bg-blue-600 rounded-full flex items-center justify-center">
          <span class="text-white text-3xl font-bold">{{ payment.club_name.substring(0, 2).toUpperCase() }}</span>
        </div>
      </div>

      <!-- Waiting for Payment Confirmation Banner -->
      <div v-if="waitingForPayment && payment.status !== 'COMPLETED'" class="mb-6 bg-blue-50 border-2 border-blue-300 rounded-lg p-6">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
          <div class="ml-4 flex-1">
            <h3 class="text-lg font-semibold text-blue-900 mb-2">
              {{ t('publicAccessPayment.waitingTitle') }}
            </h3>
            <p class="text-sm text-blue-700">
              {{ t('publicAccessPayment.waitingMessage') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Payment Status -->
      <div class="mb-6 text-center">
        <div v-if="payment.status === 'COMPLETED'" class="inline-block px-8 py-4 bg-green-100 text-green-800 text-2xl font-bold rounded border-2 border-green-300">
          {{ t('publicPayment.paid') }}
        </div>
        <div v-else class="inline-block px-8 py-4 bg-red-100 text-red-800 text-2xl font-bold rounded border-2 border-red-300">
          {{ t('publicPayment.unpaid') }}
        </div>
      </div>

      <!-- Payment URL -->
      <div class="mb-6 max-w-xl mx-auto">
        <div class="flex flex-col gap-2">
          <input
            type="text"
            :value="currentUrl"
            disabled
            class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded bg-gray-100 text-gray-700"
          >
          <button
            @click="copyPaymentUrl"
            class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 border-2 border-blue-600"
          >
            {{ t('publicPayment.copyLink') }}
          </button>
        </div>
      </div>

      <!-- Auto-refresh message for unpaid -->
      <div v-if="payment.status !== 'COMPLETED'" class="mb-6 text-center">
        <p class="text-sm text-gray-700">
          {{ t('publicPayment.autoRefreshMessage', { seconds: countdown }) }}
        </p>
      </div>

      <!-- Payment Information -->
      <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6 mb-6">
        <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('publicPayment.paymentInformation') }}</h2>
        <div class="space-y-3">
          <div class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ t('publicAccessPayment.club') }}</span>
            <span class="text-sm text-gray-900">{{ payment.club_name }}</span>
          </div>
          <div class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ t('publicAccessPayment.createdAt') }}</span>
            <span class="text-sm text-gray-900">{{ formatDate(payment.created_at) }}</span>
          </div>

          <!-- Kwota -->
          <div class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ t('publicAccessPayment.netAmount') }}</span>
            <span class="text-sm text-gray-900 font-semibold">{{ clubAccessService.formatAmount(payment.amount, payment.currency) }}</span>
          </div>

          <!-- VAT -->
          <div class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ payment.vat > 0 ? 'VAT (23%)' : 'VAT' }}</span>
            <span class="text-sm text-gray-900">{{ clubAccessService.formatAmount(payment.vat, payment.currency) }}</span>
          </div>

          <!-- Łącznie do zapłaty -->
          <div class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between py-2">
            <span class="text-sm font-medium text-gray-700">{{ t('publicAccessPayment.totalToPay') }}</span>
            <span class="text-sm text-gray-900 font-bold text-green-600">{{ clubAccessService.formatAmount(payment.total_amount, payment.currency) }}</span>
          </div>

          <!-- Informacje o zapłacie (tylko dla zapłaconych) -->
          <div v-if="payment.status === 'COMPLETED' && payment.paid_at" class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between py-2 border-t border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ t('publicAccessPayment.paidAt') }}</span>
            <span class="text-sm font-semibold text-green-600">{{ formatDate(payment.paid_at) }}</span>
          </div>
        </div>
      </div>

      <!-- Access Already Paid Message -->
      <div v-if="payment.status === 'COMPLETED' && payment.total_amount === 0" class="mb-6 max-w-xl mx-auto">
        <div class="bg-green-50 border-2 border-green-200 rounded-lg p-6">
          <div class="flex items-start gap-3">
            <svg class="h-8 w-8 text-green-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-green-900 mb-2">
                {{ t('publicAccessPayment.accessPaid') }}
              </h3>
              <p class="text-sm text-green-800">
                {{ t('publicAccessPayment.accessPaidMessage', { date: formatDate(payment.access_to) }) }}
              </p>
              <p class="text-sm text-green-700 mt-2">
                {{ t('publicAccessPayment.renewalInfo') }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Document Acceptances and Payment Buttons -->
      <div v-if="payment.status !== 'COMPLETED' && hasOnlinePayment" class="mb-6 max-w-xl mx-auto">
        <!-- Email and Document acceptances -->
        <div class="bg-white border-2 border-gray-300 rounded-lg p-4 mb-4 space-y-4">
          <!-- Name field (shown when not logged in or no name available) -->
          <div v-if="!isLoggedIn || !payerName">
            <label for="payer-name" class="block text-sm font-medium text-gray-700 mb-2">
              {{ t('publicPayment.payerName') }} <span class="text-red-600">*</span>
            </label>
            <input
              id="payer-name"
              v-model="payerName"
              type="text"
              required
              :placeholder="t('publicPayment.payerNamePlaceholder')"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <!-- Email field -->
          <div>
            <label for="payer-email" class="block text-sm font-medium text-gray-700 mb-2">
              {{ t('publicPayment.emailLabel') }} <span class="text-red-600">*</span>
            </label>
            <input
              id="payer-email"
              v-model="payerEmail"
              type="email"
              required
              :placeholder="t('publicPayment.emailPlaceholder')"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p class="mt-1 text-xs text-gray-500">{{ t('publicAccessPayment.emailHint') }}</p>
          </div>

          <!-- Document acceptances -->
          <div class="space-y-3 pt-2 border-t border-gray-200">
            <!-- Terms -->
            <div class="flex items-start gap-3">
              <input
                id="accept-terms"
                v-model="acceptTerms"
                type="checkbox"
                class="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label for="accept-terms" class="text-sm text-gray-900 cursor-pointer flex-1">
                {{ t('publicAccessPayment.agreeWith') }}
                <a
                  href="/payment-terms"
                  target="_blank"
                  class="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                >
                  {{ t('publicAccessPayment.termsLink') }}
                </a>
              </label>
            </div>

            <!-- Privacy -->
            <div class="flex items-start gap-3">
              <input
                id="accept-privacy"
                v-model="acceptPrivacy"
                type="checkbox"
                class="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label for="accept-privacy" class="text-sm text-gray-900 cursor-pointer flex-1">
                {{ t('publicAccessPayment.agreeWith') }}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  class="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                >
                  {{ t('publicAccessPayment.privacyLink') }}
                </a>
              </label>
            </div>

            <!-- App Terms -->
            <div class="flex items-start gap-3">
              <input
                id="accept-app-terms"
                v-model="acceptAppTerms"
                type="checkbox"
                class="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label for="accept-app-terms" class="text-sm text-gray-900 cursor-pointer flex-1">
                {{ t('publicAccessPayment.agreeWith') }}
                <a
                  href="/terms"
                  target="_blank"
                  class="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                >
                  {{ t('publicAccessPayment.appTermsLink') }}
                </a>
              </label>
            </div>
          </div>
        </div>

        <!-- Payment Provider Buttons -->
        <div class="space-y-3">
          <!-- PayU Button (PLN only) -->
          <template v-if="payment.currency === 'PLN'">
            <button
              @click="handlePayOnline('PAYU')"
              :disabled="!allDocumentsAccepted || processingPayment"
              class="w-full px-6 py-3 bg-green-600 text-base font-semibold text-white rounded-lg hover:bg-green-700 border-2 border-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <div v-if="processingPayment" class="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <template v-else>
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                </svg>
                {{ t('publicAccessPayment.payWithPayU') }}
              </template>
            </button>

            <!-- PayU Logo -->
            <div class="flex items-center justify-center py-2 bg-white border border-gray-200 rounded-lg">
              <img
                src="/images/payment-providers/payu.png"
                alt="PayU"
                class="h-8 object-contain"
              />
            </div>
          </template>

          <!-- Stripe Button -->
          <button
            @click="handlePayOnline('STRIPE')"
            :disabled="!allDocumentsAccepted || processingPayment"
            class="w-full px-6 py-3 bg-indigo-600 text-base font-semibold text-white rounded-lg hover:bg-indigo-700 border-2 border-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <div v-if="processingPayment" class="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            <template v-else>
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
              </svg>
              {{ t('publicAccessPayment.payWithStripe') }}
            </template>
          </button>

          <!-- Stripe Logo -->
          <div class="flex items-center justify-center py-2 bg-white border border-gray-200 rounded-lg">
            <img
              src="/images/payment-providers/stripe.png"
              alt="Stripe"
              class="h-8 object-contain"
            />
          </div>
        </div>

        <p v-if="!allDocumentsAccepted && !processingPayment" class="text-xs text-red-600 text-center mt-2">
          {{ t('publicAccessPayment.acceptAllDocuments') }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Prose styles for document content */
.prose {
  color: #374151;
  line-height: 1.75;
}

.prose h1,
.prose h2,
.prose h3,
.prose h4,
.prose h5,
.prose h6 {
  color: #111827;
  font-weight: 600;
  margin-top: 1.5em;
  margin-bottom: 0.5em;
}

.prose p {
  margin-top: 0.75em;
  margin-bottom: 0.75em;
}

.prose ul,
.prose ol {
  margin-top: 0.75em;
  margin-bottom: 0.75em;
  padding-left: 1.5em;
}

.prose li {
  margin-top: 0.25em;
  margin-bottom: 0.25em;
}

.prose strong {
  font-weight: 600;
  color: #111827;
}

.prose a {
  color: #2563eb;
  text-decoration: underline;
}

.prose a:hover {
  color: #1d4ed8;
}
</style>
